import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PracticeMap from "../components/PracticeMap";
import ReferralCharts from "../components/ReferralCharts";

const API = process.env.REACT_APP_API;

const STATUS_META = {
  active: { label: "Active referrer", badge: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  prospect: { label: "Prospect", badge: "bg-blue-100 text-blue-800 border-blue-300" },
  dormant: { label: "Dormant", badge: "bg-gray-100 text-gray-600 border-gray-300" },
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

function payerBadge(item) {
  const tier = item?.private_payer_tier;
  if (!tier) return <span className="text-gray-400 text-xs">—</span>;
  const score = item?.private_payer_score;
  const cls =
    tier === "Commercial-leaning"
      ? "bg-emerald-100 text-emerald-800"
      : tier === "Mixed"
      ? "bg-yellow-100 text-yellow-800"
      : "bg-orange-100 text-orange-800";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {tier}
      {score != null ? ` · ${Math.round(score)}` : ""}
    </span>
  );
}

function PhoneIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" />
    </svg>
  );
}

function isDue(link) {
  if (!link.next_contact_at) return false;
  return link.next_contact_at <= new Date().toISOString().slice(0, 10);
}

export default function Dashboard() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState(null); // 'star' | 'provider' | 'payer' | 'referrals'
  const [sortDir, setSortDir] = useState("desc");

  // Add-referrer search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchState, setSearchState] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Inline editing
  const [editingNotes, setEditingNotes] = useState(null); // link id
  const [notesDraft, setNotesDraft] = useState("");

  // Referral log viewer/editor (expand per referrer)
  const [logViewFor, setLogViewFor] = useState(null); // referrer_id with open log
  const [logEvents, setLogEvents] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const toggleLogView = async (link) => {
    if (logViewFor === link.referrer_id) {
      setLogViewFor(null);
      return;
    }
    setLogViewFor(link.referrer_id);
    setLogLoading(true);
    try {
      const res = await axios.get(`${API}/referral-events`, {
        params: { referrer_id: link.referrer_id, limit: 100 },
        headers: authHeaders(),
      });
      setLogEvents(res.data.events || []);
    } catch (err) {
      console.error("Failed to load referral log:", err);
      setLogEvents([]);
    } finally {
      setLogLoading(false);
    }
  };

  const patchEvent = async (eventId, patch) => {
    try {
      const res = await axios.patch(`${API}/referral-events/${eventId}`, patch, { headers: authHeaders() });
      setLogEvents((prev) => prev.map((e) => (e.id === eventId ? res.data : e)));
      loadLinks();
    } catch (err) {
      console.error("Failed to edit referral:", err);
      alert(err.response?.data?.error || "Failed to edit referral");
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Delete this logged referral?")) return;
    try {
      await axios.delete(`${API}/referral-events/${eventId}`, { headers: authHeaders() });
      setLogEvents((prev) => prev.filter((e) => e.id !== eventId));
      loadLinks();
    } catch (err) {
      console.error("Failed to delete referral:", err);
    }
  };

  // Per-referral quick log
  const [loggingFor, setLoggingFor] = useState(null); // link id with open log form
  const [logForm, setLogForm] = useState({ insurance: "", appropriateness: "2", surgical: "yes", event_date: "", patient_zip: "" });
  const [logSaving, setLogSaving] = useState(false);
  const [justLogged, setJustLogged] = useState(null); // link id flash

  const openLogForm = (linkId) => {
    setLoggingFor(linkId);
    setLogForm({ insurance: "", appropriateness: "2", surgical: "yes", event_date: new Date().toISOString().slice(0, 7), patient_zip: "" });
  };

  const saveReferralEvent = async (link) => {
    if (!logForm.insurance) {
      alert("Select the patient's insurance first (use Other / unknown if unsure).");
      return;
    }
    try {
      setLogSaving(true);
      await axios.post(
        `${API}/referral-events`,
        {
          referrer_id: link.referrer_id,
          insurance: logForm.insurance || null,
          appropriateness: logForm.appropriateness === "" ? null : parseInt(logForm.appropriateness, 10),
          surgical: logForm.surgical === "" ? null : logForm.surgical === "yes",
          patient_zip: logForm.patient_zip || null,
          event_date: logForm.event_date || null,
        },
        { headers: authHeaders() }
      );
      setLoggingFor(null);
      setJustLogged(link.id);
      setTimeout(() => setJustLogged(null), 1500);
      await loadLinks();
    } catch (err) {
      console.error("Failed to log referral:", err);
      alert(err.response?.data?.error || "Failed to log referral");
    } finally {
      setLogSaving(false);
    }
  };

  // Single "Log a Referral" card
  const [lrName, setLrName] = useState("");
  const [lrState, setLrState] = useState("");
  const [lrCandidates, setLrCandidates] = useState([]);
  const [lrSelected, setLrSelected] = useState(null);
  const [lrForm, setLrForm] = useState({ insurance: "", month: new Date().toISOString().slice(0, 7), patient_zip: "", appropriateness: "2", surgical: "yes" });
  const [lrMsg, setLrMsg] = useState(null);
  const [lrSaving, setLrSaving] = useState(false);

  useEffect(() => {
    if (lrSelected || lrName.trim().length < 2) {
      setLrCandidates([]);
      return;
    }
    const timer = setTimeout(() => {
      axios
        .get(`${API}/my-referrers/match`, {
          params: { q: lrName.trim(), state: lrState.trim() || undefined },
          headers: authHeaders(),
        })
        .then((res) => setLrCandidates(res.data.candidates || []))
        .catch(() => setLrCandidates([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [lrName, lrState, lrSelected]);

  const saveSingleReferral = async (e) => {
    e.preventDefault();
    if (!lrSelected) {
      setLrMsg({ ok: false, text: "Pick the referring provider first." });
      return;
    }
    if (!lrForm.insurance) {
      setLrMsg({ ok: false, text: "Select the patient's insurance." });
      return;
    }
    try {
      setLrSaving(true);
      await axios.post(
        `${API}/referral-events`,
        {
          referrer_id: lrSelected.referrer_id,
          insurance: lrForm.insurance,
          appropriateness: lrForm.appropriateness === "" ? null : parseInt(lrForm.appropriateness, 10),
          surgical: lrForm.surgical === "" ? null : lrForm.surgical === "yes",
          patient_zip: lrForm.patient_zip || null,
          event_date: lrForm.month || null,
        },
        { headers: authHeaders() }
      );
      setLrMsg({ ok: true, text: `Referral from ${lrSelected.name} logged.` });
      setLrSelected(null);
      setLrName("");
      setLrForm((f) => ({ ...f, patient_zip: "" }));
      await loadLinks();
    } catch (err) {
      setLrMsg({ ok: false, text: err.response?.data?.error || "Failed to log referral" });
    } finally {
      setLrSaving(false);
    }
  };

  // Batch upload
  const [batchFile, setBatchFile] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  const uploadBatch = async (e) => {
    e.preventDefault();
    if (!batchFile) return;
    try {
      setBatchBusy(true);
      setBatchResult(null);
      const form = new FormData();
      form.append("file", batchFile);
      const res = await axios.post(`${API}/referral-events/batch`, form, { headers: authHeaders() });
      setBatchResult(res.data);
      setBatchFile(null);
      await loadLinks();
    } catch (err) {
      setBatchResult({ error: err.response?.data?.error || "Upload failed" });
    } finally {
      setBatchBusy(false);
    }
  };

  // Manual provider entry
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: "", organization: "", specialty: "", city: "", state: "", zip_code: "", phone: "", status: "active",
  });

  const setManualField = (field) => (e) =>
    setManualForm((prev) => ({ ...prev, [field]: e.target.value }));

  const saveManual = async (e) => {
    e.preventDefault();
    if (!manualForm.name.trim()) return;
    try {
      setManualSaving(true);
      await axios.post(`${API}/my-referrers/manual`, manualForm, { headers: authHeaders() });
      setManualOpen(false);
      setManualForm({ name: "", organization: "", specialty: "", city: "", state: "", zip_code: "", phone: "", status: "active" });
      await loadLinks();
    } catch (err) {
      console.error("Manual add failed:", err);
      alert(err.response?.data?.error || "Failed to add provider");
    } finally {
      setManualSaving(false);
    }
  };

  const [zipSummary, setZipSummary] = useState(null);
  const [eventSummary, setEventSummary] = useState(null);
  const [todoIds, setTodoIds] = useState(new Set()); // referrer ids on the call list

  const loadLinks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/my-referrers`, { headers: authHeaders() });
      setLinks(res.data.links || []);
      setError("");
      axios
        .get(`${API}/referral-events/zip-summary`, { headers: authHeaders() })
        .then((zs) => setZipSummary(zs.data))
        .catch(() => setZipSummary(null));
      axios
        .get(`${API}/referral-events/summary`, { headers: authHeaders() })
        .then((es) => setEventSummary(es.data))
        .catch(() => setEventSummary(null));
      axios
        .get(`${API}/outreach/todo`, { headers: authHeaders() })
        .then((td) => setTodoIds(new Set((td.data.items || []).map((i) => i.referrer_id))))
        .catch(() => {});
    } catch (err) {
      console.error("Failed to load referrers:", err);
      setError("Could not load your referrers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!searchTerm.trim()) return;
    try {
      setSearching(true);
      setSearchOpen(true);
      const params = { name: searchTerm.trim(), limit: 15 };
      if (searchState.trim()) params.state = searchState.trim().toUpperCase();
      const res = await axios.get(`${API}/npi/search`, { params, headers: authHeaders() });
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Search failed:", err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addLink = async (result, status) => {
    try {
      const body = result.referrer_id != null
        ? { referrer_id: result.referrer_id, status }
        : {
            npi: result.npi,
            name: result.name,
            specialty: result.specialty,
            city: result.city,
            state: result.state,
            zip_code: result.zip_code,
            phone: result.phone,
            status,
          };
      await axios.post(`${API}/my-referrers`, body, { headers: authHeaders() });
      await loadLinks();
    } catch (err) {
      console.error("Failed to add referrer:", err);
      alert(err.response?.data?.error || "Failed to add referrer");
    }
  };

  const patchLink = async (linkId, patch) => {
    try {
      const res = await axios.patch(`${API}/my-referrers/${linkId}`, patch, {
        headers: authHeaders(),
      });
      // The PATCH response doesn't carry the computed referral_stats —
      // preserve them so the Referrals column doesn't blank out.
      setLinks((prev) => prev.map((l) => (l.id === linkId ? { ...res.data, referral_stats: l.referral_stats } : l)));
    } catch (err) {
      console.error("Failed to update referrer:", err);
    }
  };

  const addToCallList = async (link) => {
    try {
      await axios.post(
        `${API}/outreach/todo`,
        { referrer_id: link.referrer_id },
        { headers: authHeaders() }
      );
      setTodoIds((prev) => new Set([...prev, link.referrer_id]));
    } catch (err) {
      console.error("Failed to add to call list:", err);
      alert(err.response?.data?.error || "Failed to add to call list");
    }
  };

  const removeLink = async (linkId) => {
    if (!window.confirm("Remove this referrer from your list?")) return;
    try {
      await axios.delete(`${API}/my-referrers/${linkId}`, { headers: authHeaders() });
      setLinks((prev) => prev.filter((l) => l.id !== linkId));
    } catch (err) {
      console.error("Failed to remove referrer:", err);
    }
  };

  const linkedIds = new Set(links.map((l) => l.referrer_id));
  const filteredLinks =
    statusFilter === "all"
      ? links
      : statusFilter === "due"
      ? links.filter(isDue)
      : links.filter((l) => l.status === statusFilter);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      // Sensible first direction per column: names A→Z, everything else biggest-first.
      setSortDir(key === "provider" ? "asc" : "desc");
    }
  };

  const sortArrow = (key) => (sortBy === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  const visibleLinks = React.useMemo(() => {
    if (!sortBy) return filteredLinks;
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (l) => {
      switch (sortBy) {
        case "star":
          return l.preferred ? 1 : 0;
        case "provider":
          return (l.referrer?.name || "").toLowerCase();
        case "payer": {
          const s = l.referrer?.private_payer_score;
          return s == null ? -1 : s;
        }
        case "referrals":
          return l.referral_stats?.referral_count || 0;
        default:
          return 0;
      }
    };
    return [...filteredLinks].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return (a.referrer?.name || "").localeCompare(b.referrer?.name || "");
    });
  }, [filteredLinks, sortBy, sortDir]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="text-2xl font-bold text-primary">My Referrers</h2>
        <div className="flex gap-2 text-sm">
          <Link to="/refer/radar" className="px-3 py-1.5 bg-primary text-white rounded-lg hover:opacity-90">
            Find new referrers → Radar
          </Link>
          <Link to="/refer/outreach" className="px-3 py-1.5 border border-primary text-primary rounded-lg hover:bg-green-50">
            Outreach plans
          </Link>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Your referral network — no patient data, just the providers who send you business and the ones you want to.
      </p>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          {
            key: "all",
            label: "Total Referrals",
            value: links.reduce((sum, l) => sum + (l.referral_stats?.referral_count || 0), 0),
          },
          { key: "active", label: "Active Referrers", value: links.filter((l) => l.status === "active").length },
        ].map((tile) => (
          <button
            key={tile.key}
            onClick={() => setStatusFilter(tile.key)}
            className={`p-3 rounded-lg border text-left transition ${
              statusFilter === tile.key ? "border-primary bg-green-50" : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <div className={`text-2xl font-bold ${tile.warn ? "text-amber-600" : "text-primary"}`}>{tile.value}</div>
            <div className="text-xs text-gray-500">{tile.label}</div>
          </button>
        ))}
      </div>

      <PracticeMap
        patientZips={Object.fromEntries((zipSummary?.zips || []).map((z) => [z.zip, z.count]))}
        providerZips={(() => {
          const byZip = {};
          links.forEach((l) => {
            const z = (l.referrer?.zip_code || "").slice(0, 5);
            if (z.length === 5) (byZip[z] = byZip[z] || []).push(l.referrer.name);
          });
          return byZip;
        })()}
      />

      <ReferralCharts summary={eventSummary} />

      {/* Logging hub: Add a Referrer / Log a Referral / Batch Upload */}
      <div className="mb-6 grid md:grid-cols-3 gap-4 items-start">
      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <h3 className="font-semibold text-sm mb-1">1 · Add a Referrer</h3>
        <p className="text-[11px] text-gray-400 mb-2">Build your list: find a provider and mark them as a referrer or prospect.</p>
        <form onSubmit={runSearch} className="flex flex-wrap gap-2">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Provider or practice name…"
            className="flex-1 min-w-[220px] border rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={searchState}
            onChange={(e) => setSearchState(e.target.value)}
            placeholder="State (e.g. IN)"
            maxLength={2}
            className="w-28 border rounded-lg px-3 py-2 text-sm"
          />
          <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm" disabled={searching}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setManualOpen((v) => !v)}
            className="text-xs text-primary hover:underline"
          >
            {manualOpen ? "− Hide manual entry" : "+ Can't find them? Add a provider manually"}
          </button>
        </div>
        {manualOpen && (
          <form onSubmit={saveManual} className="mt-2 p-3 bg-gray-50 border rounded-lg grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <input required value={manualForm.name} onChange={setManualField("name")} placeholder="Referrer's name *" className="col-span-2 border rounded px-2 py-1.5" />
            <input value={manualForm.organization} onChange={setManualField("organization")} placeholder="Practice / organization" className="col-span-2 border rounded px-2 py-1.5" />
            <input value={manualForm.specialty} onChange={setManualField("specialty")} placeholder="Specialty" className="border rounded px-2 py-1.5" />
            <select value={manualForm.status} onChange={setManualField("status")} className="border rounded px-2 py-1.5">
              <option value="active">Refers to me</option>
              <option value="prospect">Prospect</option>
            </select>
            <input value={manualForm.city} onChange={setManualField("city")} placeholder="City" className="border rounded px-2 py-1.5" />
            <input value={manualForm.state} onChange={setManualField("state")} placeholder="State" maxLength={2} className="border rounded px-2 py-1.5" />
            <input value={manualForm.zip_code} onChange={setManualField("zip_code")} placeholder="ZIP" maxLength={5} className="border rounded px-2 py-1.5" />
            <input value={manualForm.phone} onChange={setManualField("phone")} placeholder="Phone" className="border rounded px-2 py-1.5" />
            <button type="submit" disabled={manualSaving} className="col-span-2 md:col-span-4 px-3 py-1.5 bg-primary text-white rounded-lg">
              {manualSaving ? "Saving…" : "Add to my referrers"}
            </button>
          </form>
        )}
        {searchOpen && (
          <div className="mt-3 max-h-72 overflow-y-auto border rounded-lg divide-y">
            {searching && <div className="p-3 text-sm text-gray-500">Searching the national provider directory…</div>}
            {!searching && searchResults.length === 0 && (
              <div className="p-3 text-sm text-gray-500">
                No providers found. Try just a last name, check the state, or add them manually below.
              </div>
            )}
            {!searching &&
              searchResults.map((r) => (
                <div key={r.npi} className="p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="font-medium">{r.name}</span>
                    <span className="text-gray-500"> · {r.specialty}</span>
                    <span className="text-gray-400"> · {r.city}, {r.state} {r.zip_code}</span>
                    {r.organization_name && <span className="text-gray-400"> · 🏥 {r.organization_name}</span>}
                    <span className="ml-2">{payerBadge(r)}</span>
                  </div>
                  <div className="flex gap-1.5">
                    {r.referrer_id != null && linkedIds.has(r.referrer_id) ? (
                      <span className="text-xs text-emerald-700 font-medium">✓ logged</span>
                    ) : (
                      <>
                        <button
                          onClick={() => addLink(r, "active")}
                          className="px-2 py-1 bg-emerald-600 text-white rounded text-xs"
                        >
                          Refers to me
                        </button>
                        <button
                          onClick={() => addLink(r, "prospect")}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
                        >
                          Prospect
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Card 2: Log a Referral */}
      <div className="p-4 bg-white border-2 border-primary rounded-lg">
        <h3 className="font-semibold text-sm mb-1">2 · Log a Referral</h3>
        <p className="text-[11px] text-gray-400 mb-2">The daily habit: a patient was referred to you — log it in seconds.</p>
        {!lrSelected ? (
          <div>
            <div className="flex gap-2">
              <input
                value={lrName}
                onChange={(e) => setLrName(e.target.value)}
                placeholder="Who referred the patient?"
                className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm"
              />
              <input
                value={lrState}
                onChange={(e) => setLrState(e.target.value.toUpperCase())}
                placeholder="State"
                maxLength={2}
                className="w-16 border rounded-lg px-2 py-2 text-sm uppercase"
                title="Provider's state — makes directory matching reliable"
              />
            </div>
            {lrName.trim().length >= 2 && (
              <div className="mt-2 border rounded-lg divide-y max-h-44 overflow-y-auto">
                {lrCandidates.length === 0 && (
                  <div className="p-2 text-xs text-gray-400">
                    {lrState ? "No match yet — keep typing, or check the spelling." : "Searching your referrers… add a state to search the national directory too."}
                  </div>
                )}
                {lrCandidates.map((c) => (
                  <button
                    key={c.referrer_id}
                    onClick={() => { setLrSelected(c); setLrMsg(null); }}
                    className="w-full text-left p-2 text-xs hover:bg-green-50"
                  >
                    <span className="font-medium">{c.name}</span>
                    {c.source === "mine" && <span className="ml-1 text-[10px] bg-emerald-600 text-white rounded px-1">your referrer</span>}
                    <div className="text-gray-500">{c.specialty} · {c.city}, {c.state}{c.organization_name ? ` · ${c.organization_name}` : ""}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={saveSingleReferral} className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-2 p-2 bg-green-50 border border-green-200 rounded">
              <span className="text-xs font-medium truncate">{lrSelected.name}</span>
              <button type="button" onClick={() => setLrSelected(null)} className="text-xs text-gray-400 hover:text-red-600">change</button>
            </div>
            <select
              value={lrForm.insurance}
              onChange={(e) => setLrForm((f) => ({ ...f, insurance: e.target.value }))}
              className={`w-full border rounded-lg px-2 py-2 ${lrForm.insurance ? "" : "text-gray-400"}`}
            >
              <option value="" disabled>Choose insurance…</option>
              <option value="commercial">Commercial</option>
              <option value="medicare">Medicare</option>
              <option value="medicaid">Medicaid</option>
              <option value="workers_comp">Workers comp</option>
              <option value="self_pay">Self-pay</option>
              <option value="other">Other / unknown</option>
            </select>
            <div className="flex gap-2">
              <input type="month" value={lrForm.month} onChange={(e) => setLrForm((f) => ({ ...f, month: e.target.value }))} className="flex-1 border rounded-lg px-2 py-1.5 text-xs" title="Month only — exact dates are never stored" />
              <input value={lrForm.patient_zip} onChange={(e) => setLrForm((f) => ({ ...f, patient_zip: e.target.value.replace(/\D/g, "").slice(0, 5) }))} placeholder="Pt ZIP" className="w-20 border rounded-lg px-2 py-1.5 text-xs" />
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-gray-500">Appropriate:</span>
              {["0", "1", "2"].map((v) => (
                <button key={v} type="button" onClick={() => setLrForm((f) => ({ ...f, appropriateness: v }))}
                  className={`px-2 py-0.5 rounded border ${lrForm.appropriateness === v ? "bg-primary text-white border-primary" : "bg-white"}`}>{v}</button>
              ))}
              <span className="text-gray-500 ml-2">Surgery:</span>
              {["yes", "no"].map((v) => (
                <button key={v} type="button" onClick={() => setLrForm((f) => ({ ...f, surgical: v }))}
                  className={`px-2 py-0.5 rounded border ${lrForm.surgical === v ? "bg-primary text-white border-primary" : "bg-white"}`}>{v}</button>
              ))}
            </div>
            <button type="submit" disabled={lrSaving} className="w-full px-3 py-2 bg-primary text-white rounded-lg font-semibold">
              {lrSaving ? "Saving…" : "Log referral"}
            </button>
          </form>
        )}
        {lrMsg && <p className={`mt-2 text-xs ${lrMsg.ok ? "text-emerald-700" : "text-red-600"}`}>{lrMsg.text}</p>}
      </div>

      {/* Card 3: Batch Upload */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <h3 className="font-semibold text-sm mb-1">3 · Batch Upload</h3>
        <p className="text-[11px] text-gray-400 mb-2">Catching up? Log a whole clinic day (or month) from a spreadsheet.</p>
        <a
          href="/templates/plexys-referral-batch.xlsx"
          download
          className="inline-block mb-2 text-xs text-primary font-medium hover:underline"
        >
          ↓ Download the Excel template
        </a>
        <form onSubmit={uploadBatch} className="space-y-2">
          <input
            type="file"
            accept=".xlsx,.csv"
            onChange={(e) => setBatchFile(e.target.files[0] || null)}
            className="w-full text-xs"
          />
          <button type="submit" disabled={!batchFile || batchBusy} className="w-full px-3 py-2 bg-primary text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            {batchBusy ? "Matching & logging…" : "Upload referrals"}
          </button>
        </form>
        {batchResult && (
          <div className="mt-2 text-xs">
            {batchResult.error ? (
              <p className="text-red-600">{batchResult.error}</p>
            ) : (
              <div>
                <p className="text-emerald-700 font-medium">✓ {batchResult.created} referral{batchResult.created !== 1 ? "s" : ""} logged{batchResult.failed ? ` · ${batchResult.failed} row${batchResult.failed !== 1 ? "s" : ""} need attention` : ""}</p>
                {(batchResult.failures || []).map((f) => (
                  <p key={f.row} className="text-amber-700">Row {f.row} ({f.name}): {f.reason}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>}
      {loading ? (
        <div className="text-gray-500 text-sm">Loading your referrers…</div>
      ) : visibleLinks.length === 0 ? (
        <div className="p-8 bg-blue-50 border border-blue-200 rounded-lg text-center text-sm text-blue-800">
          {links.length === 0
            ? "No referrers logged yet. Search above, or open the Radar to find busy, commercial-leaning providers near you."
            : "Nothing matches this filter."}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-2 py-2 cursor-pointer select-none" onClick={() => toggleSort("star")} title="Sort by preferred">
                  ★{sortArrow("star")}
                </th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort("provider")} title="Sort alphabetically">
                  Provider{sortArrow("provider")}
                </th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort("payer")} title="Sort by payer mix">
                  Payer Mix{sortArrow("payer")}
                </th>
                <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort("referrals")} title="Sort by referrals logged">
                  Referrals{sortArrow("referrals")}
                </th>
                <th className="px-3 py-2">Notes</th>
                <th className="px-3 py-2">Outreach</th>
                <th className="px-3 py-2">Remove</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibleLinks.map((link) => {
                const ref = link.referrer || {};
                const due = isDue(link);
                return (
                  <tr key={link.id} className={due ? "bg-amber-50" : undefined}>
                    <td className="px-2 py-2 align-middle">
                      <button
                        onClick={() => patchLink(link.id, { preferred: !link.preferred })}
                        className={`bg-transparent hover:bg-transparent p-0 text-xl leading-none ${link.preferred ? "text-amber-400" : "text-gray-300 hover:text-amber-300"}`}
                        title={link.preferred ? "Remove from preferred referrers" : "Mark as preferred referrer"}
                      >
                        {link.preferred ? "★" : "☆"}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/refer/analytics/provider/${link.referrer_id}`} className="font-medium text-primary hover:underline" title="Open provider analytics">
                        {ref.name}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {ref.specialty} · {ref.city}, {ref.state} {ref.zip_code}
                      </div>
                      {ref.organization_name && (
                        <div className="text-[11px] text-gray-400">
                          🏥 {ref.organization_name}
                          {ref.org_member_count ? ` · ${ref.org_member_count} providers` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <input
                          type="tel"
                          defaultValue={ref.phone || ""}
                          onBlur={(e) => patchLink(link.id, { phone: e.target.value })}
                          placeholder="Practice #"
                          className="w-28 border rounded px-1.5 py-1 text-xs"
                          title="Practice phone"
                        />
                        <input
                          type="tel"
                          defaultValue={ref.mobile_phone || ""}
                          onBlur={(e) => patchLink(link.id, { mobile_phone: e.target.value })}
                          placeholder="Mobile #"
                          className="w-28 border rounded px-1.5 py-1 text-xs"
                          title="Mobile phone"
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={link.status}
                        onChange={(e) => patchLink(link.id, { status: e.target.value })}
                        className={`border rounded px-1.5 py-1 text-xs ${STATUS_META[link.status]?.badge || ""}`}
                      >
                        <option value="active">Active referrer</option>
                        <option value="prospect">Prospect</option>
                        <option value="dormant">Dormant</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">{payerBadge(ref)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <button
                            onClick={() => toggleLogView(link)}
                            className="font-bold text-white bg-primary rounded px-2 py-0.5 hover:opacity-90"
                            title="View / edit this provider's referral log"
                          >
                            {link.referral_stats?.referral_count || 0}
                            {justLogged === link.id && <span className="ml-1 text-emerald-600 text-xs">+1 ✓</span>}
                          </button>
                          {link.referral_stats ? (
                            <div className="text-[11px] text-gray-500 leading-tight">
                              {link.referral_stats.pct_commercial != null && <>{link.referral_stats.pct_commercial}% comm</>}
                              {link.referral_stats.surgical_rate != null && <> · {link.referral_stats.surgical_rate}% surg</>}
                              {link.referral_stats.avg_appropriateness != null && <> · appr {link.referral_stats.avg_appropriateness}/2</>}
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-400">none logged</div>
                          )}
                        </div>
                        <button
                          onClick={() => (loggingFor === link.id ? setLoggingFor(null) : openLogForm(link.id))}
                          className="px-1.5 py-0.5 bg-primary text-white rounded text-[11px] whitespace-nowrap"
                          title="Log a referral from this provider"
                        >
                          {loggingFor === link.id ? "×" : "+ Log"}
                        </button>
                      </div>
                      {loggingFor === link.id && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded space-y-1.5 text-xs">
                          <div className="flex items-center gap-1.5">
                            <select value={logForm.insurance} onChange={(e) => setLogForm((f) => ({ ...f, insurance: e.target.value }))} className={`border rounded px-1 py-0.5 ${logForm.insurance ? "" : "text-gray-400"}`}>
                              <option value="" disabled>Choose insurance…</option>
                              <option value="commercial">Commercial</option>
                              <option value="medicare">Medicare</option>
                              <option value="medicaid">Medicaid</option>
                              <option value="workers_comp">Workers comp</option>
                              <option value="self_pay">Self-pay</option>
                              <option value="other">Other / unknown</option>
                            </select>
                            <input type="month" value={logForm.event_date} onChange={(e) => setLogForm((f) => ({ ...f, event_date: e.target.value }))} className="border rounded px-1 py-0.5" title="Month only — exact dates are never stored" />
                            <input value={logForm.patient_zip} onChange={(e) => setLogForm((f) => ({ ...f, patient_zip: e.target.value.replace(/\D/g, "").slice(0, 5) }))} placeholder="Pt ZIP" className="border rounded px-1 py-0.5 w-16" title="Patient's home ZIP (optional)" />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-500">Appropriate:</span>
                            {["0", "1", "2"].map((v) => (
                              <button key={v} onClick={() => setLogForm((f) => ({ ...f, appropriateness: v }))}
                                className={`px-1.5 py-0.5 rounded border ${logForm.appropriateness === v ? "bg-primary text-white border-primary" : "bg-white"}`}>
                                {v}
                              </button>
                            ))}
                            <span className="text-gray-500 ml-1">Surgery:</span>
                            {["yes", "no"].map((v) => (
                              <button key={v} onClick={() => setLogForm((f) => ({ ...f, surgical: v }))}
                                className={`px-1.5 py-0.5 rounded border ${logForm.surgical === v ? "bg-primary text-white border-primary" : "bg-white"}`}>
                                {v}
                              </button>
                            ))}
                          </div>
                          <button disabled={logSaving} onClick={() => saveReferralEvent(link)}
                            className="w-full px-2 py-1 bg-emerald-600 text-white rounded font-medium">
                            {logSaving ? "Saving…" : "Save referral"}
                          </button>
                        </div>
                      )}
                      {logViewFor === link.referrer_id && (
                        <div className="mt-2 p-2 bg-gray-50 border rounded text-xs space-y-1 max-h-56 overflow-y-auto" style={{ minWidth: "340px" }}>
                          <div className="font-medium text-gray-600 mb-1">Referral log — click any field to edit</div>
                          {logLoading && <div className="text-gray-400">Loading…</div>}
                          {!logLoading && logEvents.length === 0 && <div className="text-gray-400">No referrals logged.</div>}
                          {!logLoading &&
                            logEvents.map((ev) => (
                              <div key={ev.id} className="flex items-center gap-1 flex-wrap bg-white border rounded px-1.5 py-1">
                                <input
                                  type="month"
                                  value={(ev.event_date || "").slice(0, 7)}
                                  onChange={(e) => patchEvent(ev.id, { event_date: e.target.value })}
                                  className="border rounded px-1 py-0.5"
                                  title="Month only — exact dates are never stored"
                                />
                                <select value={ev.insurance || ""} onChange={(e) => patchEvent(ev.id, { insurance: e.target.value || null })} className="border rounded px-1 py-0.5">
                                  <option value="">— ins —</option>
                                  <option value="commercial">Commercial</option>
                                  <option value="medicare">Medicare</option>
                                  <option value="medicaid">Medicaid</option>
                                  <option value="workers_comp">Workers comp</option>
                                  <option value="self_pay">Self-pay</option>
                                  <option value="other">Other</option>
                                </select>
                                <select value={ev.appropriateness == null ? "" : String(ev.appropriateness)} onChange={(e) => patchEvent(ev.id, { appropriateness: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="border rounded px-1 py-0.5" title="Appropriateness">
                                  <option value="">— appr —</option>
                                  <option value="0">0</option>
                                  <option value="1">1</option>
                                  <option value="2">2</option>
                                </select>
                                <select value={ev.surgical == null ? "" : ev.surgical ? "yes" : "no"} onChange={(e) => patchEvent(ev.id, { surgical: e.target.value === "" ? null : e.target.value === "yes" })} className="border rounded px-1 py-0.5" title="Surgery">
                                  <option value="">— surg —</option>
                                  <option value="yes">Surg: yes</option>
                                  <option value="no">Surg: no</option>
                                </select>
                                <input
                                  defaultValue={ev.patient_zip || ""}
                                  onBlur={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 5); if (v !== (ev.patient_zip || "")) patchEvent(ev.id, { patient_zip: v || null }); }}
                                  placeholder="Pt ZIP"
                                  className="border rounded px-1 py-0.5 w-14"
                                />
                                <button onClick={() => deleteEvent(ev.id)} className="bg-gray-500 text-white rounded px-1.5 py-0.5 hover:bg-red-600" title="Delete this entry">✕</button>
                              </div>
                            ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-[220px]">
                      {editingNotes === link.id ? (
                        <textarea
                          autoFocus
                          value={notesDraft}
                          onChange={(e) => setNotesDraft(e.target.value)}
                          onBlur={() => {
                            patchLink(link.id, { notes: notesDraft || null });
                            setEditingNotes(null);
                          }}
                          className="w-full border rounded px-1.5 py-1 text-xs"
                          rows={2}
                          placeholder="Provider notes only — never enter patient information"
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setEditingNotes(link.id);
                            setNotesDraft(link.notes || "");
                          }}
                          className="text-left text-xs w-full bg-primary text-white rounded px-2 py-1 hover:opacity-90"
                          title="Provider notes only — never enter patient information"
                        >
                          {link.notes ? link.notes.slice(0, 80) + (link.notes.length > 80 ? "…" : "") : "+ add note"}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {todoIds.has(link.referrer_id) ? (
                        <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-600 text-white rounded px-2 py-1" title="On your call list">
                          <PhoneIcon /> On list
                        </span>
                      ) : (
                        <button
                          onClick={() => addToCallList(link)}
                          className="inline-flex items-center gap-1 text-[11px] bg-primary text-white rounded px-2 py-1 hover:opacity-90"
                          title="Add to your call list in Outreach"
                        >
                          <PhoneIcon /> Reach Out
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() => removeLink(link.id)}
                        className="text-[11px] bg-red-600 text-white rounded px-2 py-1 hover:bg-red-700"
                        title="Remove this referrer from your list"
                      >
                        ✕ Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
